import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AuthProvider } from '@/context/AuthContext';
import { NotificationProvider } from '@/context/NotificationContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import LoginPage from '@/pages/auth/LoginPage';
import LandingPage from '@/pages/LandingPage';
import AppShell from '@/components/layout/AppShell';
import WarrantyVerifyPage from '@/pages/warranty/WarrantyVerifyPage';
import PortalJoinPage from '@/pages/portal/PortalJoinPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
const GalleryPage = lazy(() => import('@/pages/gallery/GalleryPage'));
const ColorCatalogPage = lazy(() => import('@/pages/colors/ColorCatalogPage'));
const SupplierViewPage = lazy(() => import('@/pages/supplier/SupplierViewPage'));
const SignHandoverPage = lazy(() => import('@/pages/sign/SignHandoverPage'));

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const user = useCurrentUser();
  return user ? (
    <NotificationProvider>{children}</NotificationProvider>
  ) : (
    <Navigate to="/" replace />
  );
}

function PublicHome() {
  const user = useCurrentUser();
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/verify/warranty/:identifier" element={<WarrantyVerifyPage />} />
      <Route path="/portal/join/:jobId?" element={<PortalJoinPage />} />
      <Route
        path="/gallery"
        element={
          <Suspense fallback={null}>
            <GalleryPage />
          </Suspense>
        }
      />
      <Route
        path="/colors"
        element={
          <Suspense fallback={null}>
            <ColorCatalogPage />
          </Suspense>
        }
      />
      <Route
        path="/supplier/:token"
        element={
          <Suspense fallback={null}>
            <SupplierViewPage />
          </Suspense>
        }
      />
      <Route
        path="/sign/:token"
        element={
          <Suspense fallback={null}>
            <SignHandoverPage />
          </Suspense>
        }
      />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppShell />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <SpeedInsights />
    </AuthProvider>
  );
}
